import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { GigServiceType, PrivateGigConstants } from "../constants/gigConstants";
import { saveDraftGig } from "../firebase/gig";
import { ExtraInformation, Gig, GigPublicDetails } from "../interfaces/Gig";
import { GoogleMapResultData } from "../interfaces/GoogleMap";
import { RootState } from "./store";

export interface createGigState {
  gig: Gig;
  editGigId?: string;
}

const initialState: createGigState = {
  gig: {
    serviceType: GigServiceType.OnGoingMealPrep,
    isPrivate: false,
    invitedChefUids: [],
    privateInviteLimit: PrivateGigConstants.InviteLimit,
  },
  editGigId: undefined,
};

export const createGigSlice = createSlice({
  name: "createGig",
  initialState,
  reducers: {
    setServiceType: (state, action: PayloadAction<GigServiceType>) => {
      state.gig.serviceType = action.payload;
    },
    setLocation: (state, action: PayloadAction<GoogleMapResultData>) => {
      state.gig.location = action.payload;
    },
    setDate: (state, action: PayloadAction<string | undefined>) => {
      state.gig.date = action.payload;
    },
    setMultiDate: (
      state,
      action: PayloadAction<Gig["multiDate"] | undefined>
    ) => {
      state.gig.multiDate = action.payload;
    },
    setPublicDetails: (state, action: PayloadAction<GigPublicDetails>) => {
      state.gig.title = action.payload.title;
      state.gig.headerImageUrl = action.payload.headerImageUrl;
      state.gig.details = action.payload.details;
      state.gig.preference = action.payload.preference;
      state.gig.allergies = action.payload.allergies;
      state.gig.tags = action.payload.tags;
      state.gig.numberOfGuests = action.payload.numberOfGuests;
      state.gig.budget = action.payload.budget;
      state.gig.isPrivate = action.payload.isPrivate ?? false;
      state.gig.privateInviteLimit = state.gig.isPrivate
        ? PrivateGigConstants.InviteLimit
        : undefined;
      if (!state.gig.isPrivate) {
        state.gig.invitedChefUids = [];
      }
    },
    // ! Fix the FormData error and add type in payload
    setServiceStyleAndMealType: (state, action) => {
      state.gig.serviceStyle = action.payload.serviceStyle;
      state.gig.mealsDesired = action.payload.mealsDesired;
      state.gig.additionalNotes = action.payload.additionalNotes;
      state.gig.additionalServices = action.payload.additionalServices;
    },
    setExtraInformation: (state, action: PayloadAction<ExtraInformation>) => {
      state.gig.venueInformation = action.payload.venueInformation;
      state.gig.mealsRequested = action.payload.mealsRequested;
      state.gig.notesOnBreakfast = action.payload.notesOnBreakfast;
      state.gig.notesOnLunch = action.payload.notesOnLunch;
      state.gig.notesOnDinner = action.payload.notesOnDinner;
      state.gig.travelInfo = action.payload.travelInfo;
      state.gig.eventIntention = action.payload.eventIntention;
    },
    setInvitedChefUids: (state, action: PayloadAction<string[]>) => {
      const uniqueChefIds = Array.from(new Set(action.payload));
      const limit =
        state.gig.privateInviteLimit ?? PrivateGigConstants.InviteLimit;
      state.gig.invitedChefUids = uniqueChefIds.slice(0, limit);
      if (state.gig.invitedChefUids.length > 0) {
        state.gig.isPrivate = true;
        state.gig.privateInviteLimit = limit;
      }
    },
    setGig: (state, action: PayloadAction<Gig>) => {
      state.gig = {
        ...action.payload,
        invitedChefUids: action.payload.invitedChefUids ?? [],
        isPrivate: action.payload.isPrivate ?? false,
        privateInviteLimit:
          action.payload.privateInviteLimit ?? PrivateGigConstants.InviteLimit,
      };
    },
    setEditGigId: (state, action: PayloadAction<string>) => {
      state.editGigId = action.payload;
    },

    resetCreateGigState: () => initialState,
  },
});

export const saveGig = createAsyncThunk(
  "gigs/saveGig",
  async (userUid: string, { getState }) => {
    const state = getState() as RootState;
    await saveDraftGig({ ...state.createGig.gig }, userUid);
  }
);

export const {
  setServiceType,
  setLocation,
  setDate,
  setMultiDate,
  setPublicDetails,
  setServiceStyleAndMealType,
  setExtraInformation,
  setInvitedChefUids,
  setGig,
  setEditGigId,
  resetCreateGigState,
} = createGigSlice.actions;

export default createGigSlice.reducer;
